<template>
	<div class="pb4">
		<div>
			<!-- Type -->
			<label for="type" class="block xs-12 lh125 mb1 fw7 fs3">
				{{ $t("type") }}
			</label>
			<j-base-input-text size="small" id="type" v-model="form.type" />

			<!-- Data Type -->
			<label for="dataType" class="block xs-12 lh125 mt4 mb1 fw7 fs3">
				{{ $t("data_type") }}
			</label>
			<j-base-select
				id="dataType"
				size="small"
				v-model="form.dataType"
				:allowSelectNothing="false"
				:options="[
					{ name: 'String (Text)', value: 'string' },
					{ name: 'Number', value: 'number' },
					{ name: 'Boolean', value: 'boolean' },
					{ name: 'Date', value: 'date' },
					{ name: 'Object', value: 'object' }
				]"
				@input="setDataTypeAndComponent"
			/>

			<!-- Value -->
			<label for="value" class="block xs-12 lh125 mt4 mb1 fw7 fs3">
				{{ $t("value") }}
			</label>

			<!-- Value -->
			<div class="flex xs-12 xs-middle">
				<component
					ref="input"
					class="xs"
					size="small"
					v-model="form.value"
					:is="component.componentName"
					v-bind="component.props"
				/>
				<j-button
					class="ml4"
					size="small"
					:isLoading="isLoading"
					type="primary"
					@click="createFact"
				>
					{{ $t("add") }}
				</j-button>
			</div>
		</div>
	</div>
</template>

<script>
import JButton from "@/components/JButton"
import JBaseInputText from "@/components/JBaseInputText"
import JBaseInputLongText from "@/components/JBaseInputLongText"
import JBaseInputNumber from "@/components/JBaseInputNumber"
import JBaseDatePickerSimple from "@/components/JBaseDatePickerSimple"
import JBaseToggle from "@/components/JBaseToggle"
import JBaseSelect from "@/components/JBaseSelect"
import CoreMonacoEditor from "@/modules/core/components/CoreMonacoEditor"

import componentsDictionary from "../componentsDictionary.js"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		JButton,
		JBaseInputText,
		JBaseInputLongText,
		JBaseInputNumber,
		JBaseDatePickerSimple,
		JBaseToggle,
		JBaseSelect,
		CoreMonacoEditor
	},

	props: {
		entityId: {
			type: String
		},

		entityName: {
			type: String,
			default: ""
		},

		/**
		 * Instructions for creation of a new fact
		 * What properties it can included is in docs
		 */
		createOptions: {
			type: Object
		}
	},

	data() {
		return {
			isLoading: false,
			form: {
				dataType: "string",
				type: "",
				value: ""
			},

			// Will be populated from setComponent method
			component: "text"
		}
	},

	methods: {
		/**
		 * Set default dataType and component
		 * Also look for createOptions and if it's there override
		 */
		init() {
			// set default dataType and component
			this.setDataTypeAndComponent("string")

			// If we have createOptions, we override
			if (!this.createOptions) return

			// Populate creation form based on createOptions instructions

			// Type
			this.form.type = this.createOptions?.type || ""

			// dataType
			this.form.dataType = this.createOptions?.dataType || "string"
			if (this.createOptions?.dataType)
				this.setDataTypeAndComponent(this.createOptions.dataType)

			// Value
			// If value is object, we are reaching for value.set
			// If value is string, we are assigning just value
			if (typeof this.createOptions?.value === "string")
				this.form.value = this.createOptions?.value || ""
			else this.form.value = this.createOptions?.value?.default

			// If createOptions.component invoke setComponent
			if (this?.createOptions?.value?.component) {
				this.setComponent(
					this?.createOptions?.value?.component,
					this?.createOptions?.value?.props || {}
				)
				return
			}

			// If we are passing customComponent
			if (this?.createOptions?.value?.componentObject?.source) {
				this.component = {
					componentName: this?.createOptions?.value?.componentObject?.source,
					props: this?.createOptions?.value?.componentObject?.source?.props
				}
			}
		},

		/**
		 * Will set component in state so that form knows what to render for value field
		 */
		setComponent(compName = "", compProps = {}) {
			const { name, props } = componentsDictionary(compName)

			this.component = {
				componentName: name,
				props: { ...props, ...compProps }
			}
		},

		async createFact() {
			const query = gql`
				mutation addFact($entityId: String!, $input: addFactInput!) {
					addFact(entityId: $entityId, factInput: $input) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
			`

			const dataType = `${this.form.dataType || "string"}Value`
			const value =
				this.form.dataType === "array" || this.form.dataType === "object"
					? JSON.parse(this.form.value)
					: this.form.value
			this.isLoading = true
			const res = await api({
				query,
				variables: {
					entityId: this.entityId,
					input: {
						type: this.form.type,
						value: { [dataType]: value }
					}
				}
			})

			// Errors
			if (res?.data?.addFact?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.addFact?.errors?.[0] || this.$t("errors.general")
				)
				this.isLoading = false
				return
			}

			// Stop loading
			this.isLoading = false

			// Reset form
			this.form = { type: "", value: "" }

			// Emit event up
			this.$emit("fact:created", res?.data?.addFact?.createdStatus?.id)
		},

		/**
		 * When changing dataType, reset values
		 */
		setDataTypeAndComponent(dataType = "string") {
			if (dataType === "string") {
				this.form.value = ""
				this.setComponent("text")
			} else if (dataType === "number") {
				this.form.value = 0
				this.setComponent("number")
			} else if (dataType === "boolean") {
				this.form.value = true
				this.setComponent("toggle")
			} else if (dataType === "date") {
				this.form.value = new Date()
				this.setComponent("date")
			} else if (dataType === "object") {
				this.form.value = JSON.stringify([
					{ key: "key", value: { stringValue: "" } }
				])
				this.setComponent("monacoEditor")
			}
		}
	},

	created() {
		this.init()
	},

	mounted() {
		this.$refs?.input?.$el?.children[0]?.focus()
	}
}
</script>
